import { useEffect, useMemo, useState } from 'react';
import { useChartsContext } from '../context';

export interface UsePanelTooltipPortalContainerProps {
  /**
   * Optional root element to use as a place to start looking upward for the closest `tooltipPortalContainerId`. This
   * should usually be the chart or other html element that the tooltip is associated with.
   *
   * If not specified, it will fall back to looking for the first `tooltipPortalContainerId` found in the document.
   */
  rootEl?: HTMLElement | null;
}

export interface UsePanelTooltipPortalContainerResponse {
  /**
   * HTML element that should be used as the portal container for the tooltip.
   *
   * May be null if a portal container element cannot be found. This generally happens in one of the
   * following use cases:
   * - `tooltipPortalContainerId` is not specified in the `chartsTheme`. Despite the name, this should be set to a
   *   css selector that is compatible with APIs like `querySelector` and `closest`.
   * - No selectors on the page match the value of `tooltipPortalContainerId`.
   */
  tooltipPortalContainerEl: HTMLElement | null;
}

/**
 * Hook to help with finding the best HTML element to be used as a tooltip portal for a given use case.
 * Requires that a `tooltipPortalContainerId` is configured in the `chartsTheme`.
 */
export const usePanelTooltipPortalContainer = ({
  rootEl,
}: UsePanelTooltipPortalContainerProps = {}): UsePanelTooltipPortalContainerResponse => {
  const {
    chartsTheme: { tooltipPortalContainerId },
  } = useChartsContext();

  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);

  // There isn't a good workaround for this without significantly re-architecting how the tooltip portal container id
  // currently works. This needs to run *after* rendering is finished to ensure the dom is up-to-date, so we can't
  // just put it in the render cycle. If we save the value to a ref, the hook won't update and return the right value
  // when the ref changes.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!tooltipPortalContainerId) {
      // No id available.
      setContainerEl(null);
      return;
    }

    // When root element is provided, look upward from the root element to find the closest `tooltipPortalContainerId`.
    if (rootEl) {
      setContainerEl(rootEl.closest<HTMLElement>(tooltipPortalContainerId));
      return;
    }

    // If no root element is provided, the first matching `tooltipPortalContainerId` in the document is used.
    setContainerEl(document.querySelector<HTMLElement>(tooltipPortalContainerId));
  }, [rootEl, tooltipPortalContainerId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  return useMemo(() => {
    return {
      tooltipPortalContainerEl: containerEl,
    };
  }, [containerEl]);
};
